import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../../../shared/hooks/useApi'
import { formatDate, getNoticeTypeLabel, truncateText } from '../../../shared/utils/formatters'
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react'

interface Notice {
  id: string
  notice_number?: string
  notice_type: string
  organization_name: string
  publication_date: string
  cpv_code?: string
  order_object?: string
}

interface Filters {
  order_object: string
  organization_name: string
  cpv_code: string
  notice_types?: string[]
  organization_city: string
  organization_province: string
  client_type: string
  order_type: string
  tender_type: string
  is_tender_amount_below_eu: boolean | null
  procedure_result: string
  bzp_number: string
  date_from: string
  date_to: string
}

interface TendersListProps {
  filters: Filters
  onNoticeClick: (noticeId: string) => void
}

interface PaginationInfo {
  page: number
  page_size: number
  total: number
}

interface NoticeCardProps {
  notice: Notice
  onViewDetail: (noticeId: string) => void
}

function NoticeCard({ notice, onViewDetail }: NoticeCardProps) {
  return (
    <div
      className="bg-surface-raised border border-border rounded-lg p-6 hover:border-border transition-colors"
      data-testid="notice-card"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-ink mb-2">
            {notice.notice_number || notice.id}
          </h3>
          <p className="text-sm text-ink-secondary mb-2">{getNoticeTypeLabel(notice.notice_type)}</p>
          <p className="text-sm text-ink-muted">{notice.organization_name}</p>
        </div>
        <button
          onClick={() => onViewDetail(notice.id)}
          className="ml-4 bg-brand-bg hover:bg-brand-bg text-brand p-2 rounded-md transition-colors"
          title="Zobacz szczegóły"
        >
          <Eye className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-2 text-sm text-ink-secondary">
        <div className="flex justify-between">
          <span>Data publikacji:</span>
          <span>{formatDate(notice.publication_date)}</span>
        </div>
        {notice.cpv_code && (
          <div className="flex justify-between">
            <span>Kod CPV:</span>
            <span>{notice.cpv_code}</span>
          </div>
        )}
      </div>

      {notice.order_object && (
        <div className="mt-4">
          <p className="text-sm text-ink-secondary">{truncateText(notice.order_object, 150)}</p>
        </div>
      )}
    </div>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, 5)
      } else if (currentPage >= totalPages - 2) {
        pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push(currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-surface-raised border-t border-border sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Poprzednia
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Następna
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-ink-secondary">
            Strona <span className="font-medium">{currentPage}</span> z{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md -space-x-px">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-surface-raised text-sm font-medium text-ink-muted hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === currentPage
                    ? 'z-10 bg-brand-bg border-brand text-brand'
                    : 'bg-surface-raised border-border text-ink-muted hover:bg-surface-hover'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-surface-raised text-sm font-medium text-ink-muted hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}

const TendersList = ({ filters, onNoticeClick }: TendersListProps) => {
  const { getNotices, loading, error } = useApi()
  const navigate = useNavigate()
  const [notices, setNotices] = useState<Notice[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    page_size: 20,
    total: 0,
  })

  const loadNotices = async (page = 1, appliedFilters: Filters = filters) => {
    try {
      const params = {
        page,
        page_size: pagination.page_size,
        ...appliedFilters,
      }

      const response = await getNotices(params)
      setNotices(response.items || [])
      setPagination({
        page: response.page,
        page_size: response.page_size,
        total: response.total,
      })
    } catch (err) {
      console.error('Failed to load notices:', err)
    }
  }

  useEffect(() => {
    loadNotices(1, filters)
  }, [filters])

  const handlePageChange = (newPage: number) => {
    loadNotices(newPage, filters)
  }

  const handleViewDetail = (noticeId: string) => {
    onNoticeClick(noticeId)
  }

  const totalPages = Math.ceil(pagination.total / pagination.page_size)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
        <p className="text-danger">Błąd podczas ładowania ogłoszeń: {error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-ink">Lista ogłoszeń</h2>
        <div className="text-sm text-ink-secondary">
          Znaleziono {pagination.total.toLocaleString('pl-PL')} ogłoszeń
        </div>
      </div>

      {notices.length === 0 ? (
        <div
          className="bg-surface border border-border rounded-lg p-8 text-center"
          data-testid="empty-state"
        >
          <p className="text-ink-secondary">Brak ogłoszeń spełniających kryteria wyszukiwania.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {notices.map((notice) => (
              <NoticeCard key={notice.id} notice={notice} onViewDetail={handleViewDetail} />
            ))}
          </div>

          <Pagination
            currentPage={pagination.page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  )
}

export default TendersList
